/**
 * Collection rate's window: six months of bills, lagged three months behind the range.
 *
 * MEASURE-RULES §4.3, replaced 2026-10-05 (Asana "Redesign Collection Rate Calculation
 * Logic"). A claim billed last month has not had time to resolve, so reading collection
 * over the months on screen made every range look worst at its end. For a range
 * [start, end] the rate is read over the bill months start − 8 … end − 3, inclusive:
 * one month M reads M−8 … M−3, the model's `Collected Percentage (Back 3 months,
 * Rolling 6 months)` anchored on the range instead of on today. A longer range widens
 * the window month for month. The story's "start date − 9 months" is that same lower
 * edge stated exclusively; read inclusively it would make a single month seven months.
 *
 * POOLED: SUM(resolved charge) / SUM(charge) over every bill month in the window — the
 * story's "rolling 6-month average" is a ratio of sums, never a mean of monthly rates.
 * scripts/build_bundle.py `collection_window` is the same arithmetic; the tests pin both.
 */
import { monthSpan } from '@/format'
import { divide, type CollectionBasis } from './core'

export const COLLECTION_LAG = 3
export const COLLECTION_SPAN = 6

/** Every label the measure goes by. Says what the window is, so nobody reads it as the month's own. */
export const COLLECTION_LABEL = 'Collection rate (6 mo, 3-mo lag)'

/** A person's bill month ('YYYY-MM') → [charge, resolved charge]. The bundle's `collectionMonths`. */
export type CollectionSeries = Record<string, readonly [number, number]>

/** 'YYYY-MM' plus n months; n may be negative. */
export function addMonths(month: string, n: number): string {
  const y = Number(month.slice(0, 4))
  const m = Number(month.slice(5, 7))
  const i = y * 12 + (m - 1) + n
  return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, '0')}`
}

export interface CollectionWindow {
  /** First bill month, inclusive. */
  from: string
  /** Last bill month, inclusive. */
  to: string
  /** Every bill month in it, in order. Six for one month; one more per extra month. */
  months: string[]
}

/** The bill months a range [start, end] reads its collection rate over. */
export function collectionWindow(start: string, end: string): CollectionWindow {
  const from = addMonths(start, -(COLLECTION_LAG + COLLECTION_SPAN - 1))
  const to = addMonths(end, -COLLECTION_LAG)
  const months: string[] = []
  for (let m = from; m <= to; m = addMonths(m, 1)) months.push(m)
  return { from, to, months }
}

/**
 * One person's resolved and total charge over the window for [start, end] — what any
 * pooled figure (team, department, org) adds up. Null when no bill month in the window
 * has a row: no basis, which is not a 0% collection rate.
 */
export function windowCollection(
  series: CollectionSeries | undefined,
  start: string,
  end: string,
): CollectionBasis | null {
  if (!series) return null
  const { from, to } = collectionWindow(start, end)
  let out: CollectionBasis | null = null
  for (const [m, [total, resolved]] of Object.entries(series)) {
    if (m < from || m > to) continue
    out = out === null ? { resolved: 0, total: 0 } : out
    out.resolved += resolved
    out.total += total
  }
  return out
}

/** The rate off a basis. */
export const collectionRateOf = (b: CollectionBasis | null) =>
  b === null ? null : divide(b.resolved, b.total)

/** "bill months May–Oct 2025 (6 months)" — the exact months, for a hover. */
export function describeWindow(w: CollectionWindow): string {
  return `bill months ${monthSpan(w.months)} (${w.months.length} months)`
}
